import { Product, Branch } from '../types';
import { supabase } from '../lib/supabase';
import { WHATSAPP_TEMPLATES } from '../config/whatsappConfig';

/**
 * Service for building contextual WhatsApp messages
 * Implements security best practices by sanitizing user input
 */
export class WhatsAppMessageService {
    /**
     * Sanitize text to prevent injection attacks
     * Removes potentially dangerous characters while preserving readability
     */
    private static sanitize(text: string): string {
        return text
            .replace(/[<>]/g, '') // Remove HTML tags
            .replace(/[{}]/g, '') // Remove curly braces
            .replace(/[\[\]]/g, '') // Remove square brackets
            .trim();
    }

    /**
     * Build general inquiry message for floating WhatsApp button
     */
    static buildGeneralMessage(): string {
        return WHATSAPP_TEMPLATES.general;
    }

    /**
     * Build message for purchasing an available product
     */
    static buildProductAvailableMessage(product: Product, branch: Branch, variantName?: string): string {
        const productName = this.sanitize(product.name);
        const variantText = variantName ? ` (Variante: ${this.sanitize(variantName)})` : '';

        return WHATSAPP_TEMPLATES.productAvailable(productName, variantText);
    }

    /**
     * Build message for inquiring about an unavailable product
     */
    static buildProductUnavailableMessage(product: Product, currentBranch: Branch, variantName?: string): string {
        const productName = this.sanitize(product.name);
        const variantText = variantName ? ` (Variante: ${this.sanitize(variantName)})` : '';

        return WHATSAPP_TEMPLATES.productUnavailable(productName, variantText);
    }

    /**
     * Build message for multiple products (Cart/List)
     */
    static buildMultiProductInquiryMessage(items: { product: Product, quantity: number }[], branch: Branch): string {
        let message = WHATSAPP_TEMPLATES.multiProductContext.header;

        items.forEach((item) => {
            const productName = this.sanitize(item.product.name);
            message += `- *${productName}* (x${item.quantity})\n`;
        });

        message += WHATSAPP_TEMPLATES.multiProductContext.footer;
        return message;
    }

    /**
     * Build message for a shipping request with customer information
     */
    static buildShippingRequestMessage(
        customerInfo: { name: string; email: string; phone: string; address: string },
        items: { product: Product; quantity: number; variantText?: string }[]
    ): string {
        let message = WHATSAPP_TEMPLATES.shippingRequest.header;

        // Customer Info
        message += WHATSAPP_TEMPLATES.shippingRequest.customerInfoHeader;
        message += `> *Nombre:* ${this.sanitize(customerInfo.name)}\n`;
        message += `> *Correo:* ${this.sanitize(customerInfo.email)}\n`;
        message += `> *Teléfono:* ${this.sanitize(customerInfo.phone)}\n`;
        message += `> *Dirección:* ${this.sanitize(customerInfo.address)}\n`;

        // Items
        message += WHATSAPP_TEMPLATES.shippingRequest.itemsHeader;
        items.forEach((item) => {
            const productName = this.sanitize(item.product.name);
            const variantText = item.variantText ? ` _(${this.sanitize(item.variantText)})_` : '';
            message += `\n- *${productName}*${variantText}\n  Cantidad: ${item.quantity}\n`;
        });

        message += WHATSAPP_TEMPLATES.shippingRequest.footer;
        return message;
    }

    /**
     * Format phone number for WhatsApp (remove spaces and special characters)
     */
    static formatPhoneForWhatsApp(phone: string): string {
        return phone.replace(/[\s\-\(\)]/g, '');
    }

    /**
     * Open WhatsApp with the given phone and message
     * Implements rate limiting to prevent spam
     */
    private static lastClickTime = 0;
    private static readonly RATE_LIMIT_MS = 1000; // 1 second

    static openWhatsApp(phone: string, message: string): boolean {
        const now = Date.now();

        // Rate limiting
        if (now - this.lastClickTime < this.RATE_LIMIT_MS) {
            console.warn('WhatsApp: Rate limit exceeded. Please wait.');
            return false;
        }

        this.lastClickTime = now;

        const formattedPhone = this.formatPhoneForWhatsApp(phone);
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
        return true;
    }

    /**
     * Track WhatsApp click event (for analytics and database)
     */
    static async trackWhatsAppClick(data: {
        source: 'floating_button' | 'product_card' | 'product_modal' | 'branch_selector';
        branch: string;
        phoneIndex: number;
        productId?: string;
        productName?: string;
        phoneNumber?: string;
        messageType: 'general' | 'buy' | 'consult';
    }): Promise<void> {
        // 1. Report to Google Analytics (Client Side only)
        if (typeof window !== 'undefined' && (window as any).gtag) {
            (window as any).gtag('event', 'whatsapp_click', {
                event_category: 'engagement',
                event_label: data.source,
                branch: data.branch,
                phone_index: data.phoneIndex,
                product_id: data.productId,
                message_type: data.messageType
            });
        }

        // 2. Persistent log in Supabase
        try {
            await supabase
                .from('whatsapp_logs')
                .insert([{
                    product_id: data.productId || null,
                    product_name: data.productName || null,
                    branch: data.branch,
                    phone_number: data.phoneNumber || null,
                    message_type: data.messageType,
                    created_at: new Date().toISOString()
                }]);
        } catch (error) {
            console.error('Error tracking WhatsApp click to database:', error);
        }
    }
}
